import blossomBg from '@/assets/image/onboarding/blossom-bg.webp';
import fieldBg from '@/assets/image/onboarding/field-bg.webp';
import meadowBg from '@/assets/image/onboarding/meadow-bg.webp';
import sunshineBg from '@/assets/image/onboarding/sunshine-bg.webp';
import Image from 'next/image';

/**
 * Per-photo blur strength. A sharp export needs the full blur the design
 * calls for; an export the design already ships blurred needs only a light
 * pass, which costs nothing visually and smooths the compression and upscale
 * artefacts that would otherwise read as pixelation on a large pane.
 */
const PHOTOS = {
  blossom: { src: blossomBg, blur: 'blur-md' },
  field: { src: fieldBg, blur: 'blur-md' },
  meadow: { src: meadowBg, blur: 'blur-2xl' },
  sunshine: { src: sunshineBg, blur: 'blur-md' },
} as const;

/**
 * Blurred photographic backdrop used by the guided-tour preview panes, in
 * place of the SVG pattern backgrounds the control arm keeps.
 */
export function OnboardingPhotoBackground({ photo }: { photo: keyof typeof PHOTOS }) {
  const { src, blur } = PHOTOS[photo];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <Image
        src={src}
        alt=""
        // The pane is taller than it is wide, so `cover` scales these portrait
        // sources by height — `sizes` has to describe that rendered width, not
        // the pane's, or the optimizer serves a candidate we then upscale.
        fill
        sizes="(min-width: 1024px) 900px, 100vw"
        // Default (75) leaves visible blocking on these smooth, blurred fields.
        quality={90}
        // scale-110 crops the soft edge — the blur's own falloff on a sharp
        // export, the baked-in white vignette on a pre-blurred one.
        className={`scale-110 object-cover ${blur}`}
      />
    </div>
  );
}
